import React, { useMemo, useState, useEffect } from "react";
import { format, parseISO } from "date-fns";

import { Drawer, IconButton, Skeleton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import CustomChip from "components/common/Chip";
import CustomTooltip from "components/common/Tooltip";

import "./HolidayDetailDrawer.scss";

const TENTATIVE_TOOLTIP_TEXT =
  "This holiday follows the lunar calendar and depends on the sighting of the moon, so the exact date may shift by a day.";

function HolidayDetailDrawer({ open, onClose, holiday }) {
  const { isMultiDay, startDate, endDate, totalDays } = holiday || {};
  const [imageLoading, setImageLoading] = useState(true);

  const formattedDate = useMemo(() => {
    if (!startDate) return "";

    if (isMultiDay) {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      const startFormatted = format(start, "EEE, MMM dd, yyyy");
      const endFormatted = format(end, "EEE, MMM dd, yyyy");
      return `${startFormatted} – ${endFormatted} (${totalDays} Days)`;
    }
    const holidayDate = parseISO(startDate);
    return format(holidayDate, "EEE, MMM dd, yyyy");
  }, [isMultiDay, startDate, endDate, totalDays]);

  const { image, isTentative, customaryGreeting, howCelebrated } = holiday || {};

  // Reset image loading state when holiday changes
  useEffect(() => {
    setImageLoading(true);
  }, [holiday?.id]);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
  };

  if (!holiday) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="holiday-detail-drawer"
      transitionDuration={300}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", sm: "500px", md: "552px" },
          },
        },
      }}
    >
      <div className="holiday-detail-container">
        <div className="holiday-detail-header">
          <IconButton onClick={onClose} className="close-button">
            <CloseIcon />
          </IconButton>
        </div>

        <div className="holiday-image-container">
          {imageLoading && (
            <Skeleton variant="rectangular" width="100%" height={300} animation="wave" sx={{ borderRadius: "8px" }} />
          )}
          {image && (
            <img
              src={image}
              alt={holiday.name}
              className="holiday-image"
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ display: imageLoading ? "none" : "block" }}
            />
          )}
        </div>

        <div className="holiday-detail-content">
          <div className="holiday-title-section">
            <p className="heading-h2-semibold color-text-heading-primary">{holiday.name}</p>
            {isTentative && (
              <CustomTooltip content={TENTATIVE_TOOLTIP_TEXT} direction="top">
                <span>
                  <CustomChip label="Tentative" size="medium" variant="filled" color="default" disabled />
                </span>
              </CustomTooltip>
            )}
          </div>

          <div className="holiday-info-row-horizontal holiday-date-row">
            <p className="para-body-m-regular color-text-body-secondary">Holiday date(s)</p>
            <p className="para-body-m-regular color-text-body-primary">{formattedDate}</p>
          </div>

          <div className="holiday-info-row-horizontal holiday-country-row">
            <p className="para-body-m-regular color-text-body-secondary">Country</p>
            <p className="para-body-m-regular color-text-body-primary">
              {Array.isArray(holiday.region) ? holiday.region.join(", ") : holiday.region}
            </p>
          </div>

          {customaryGreeting && (
            <div className="holiday-info-section-vertical">
              <p className="heading-h5-semibold color-text-heading-primary">Customary greeting</p>
              <p className="para-body-m-regular color-text-body-primary">{customaryGreeting}</p>
            </div>
          )}

          {howCelebrated && (
            <div className="holiday-info-section-vertical">
              <p className="heading-h5-semibold color-text-heading-primary">How is it celebrated?</p>
              <p className="para-body-m-regular color-text-body-primary">{howCelebrated}</p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}

export default HolidayDetailDrawer;
